import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { throwErrorMessage } from "../../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./input-phone.constants";
import { TKwikUIInputPhoneSize } from "./input-phone.definitions";
import { isValidKeyValue } from "./input-phone.validation";

@Component({
  selector: "kwikui-input-phone",
  templateUrl: "./input-phone.component.html",
  styleUrls: ["./input-phone.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUIInputPhoneComponent),
      multi: true
    }
  ]
})
export class KwikUIInputPhoneComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() countryCode: string = DEFAULT_VALUES.countryCode;

  @Input() disabled: boolean = DEFAULT_VALUES.disabled;

  @Input() focus: boolean = DEFAULT_VALUES.focus;

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName: string = DEFAULT_VALUES.formControlName;

  @Input() hintContent: string = DEFAULT_VALUES.hintContent;

  @Input() icon: string = DEFAULT_VALUES.icon;

  @Input() id: string = DEFAULT_VALUES.id;

  @Input() invalid: boolean = DEFAULT_VALUES.invalid;

  @Input() isShowLabelOutside: boolean = DEFAULT_VALUES.isShowLabelOutside;

  @Input() label: string = DEFAULT_VALUES.label;

  @Input() phoneMaskAfterCountryCode: string =
    DEFAULT_VALUES.phoneMaskAfterCountryCode;

  @Input() placeholder: string = DEFAULT_VALUES.placeholder;

  @Input() postfix: string = DEFAULT_VALUES.postfix;

  @Input() prefix: string = DEFAULT_VALUES.prefix;

  @Input() properties = { readOnly: false };

  @Input() removeCountryCodeFromValue: boolean =
    DEFAULT_VALUES.removeCountryCodeFromValue;

  @Input() size: TKwikUIInputPhoneSize = DEFAULT_VALUES.size;

  @Input() validators = { required: true };

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  formGroup: FormGroup = new FormGroup({});

  subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    this.formGroup.addControl(this.formControlName, this.formControl);
    this.focus = this.focus === undefined ? false : this.focus;
    this.invalid =
      this.invalid === undefined ? false : this.invalid && this.focus;
    this.validators = { ...this.validators };
    this.properties = { ...this.properties };
    this.setDisabled();

    this.subscriptions.add(
      this.formGroup.controls[this.formControlName].valueChanges
        .pipe(distinctUntilChanged()) // makes sure the value has actually changed.
        .subscribe((value) => this.handleInputValueChange(value))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const verifyChange = (key: string) => {
      return changes.hasOwnProperty(key) && !changes[key].firstChange;
    };

    for (const change of Object.entries(changes)) {
      const key: string = change[0];
      const value: any = change[1].currentValue;
      this.validateInputProperty(key, value);
    }

    if (verifyChange("formControl")) {
      this.formControl = changes.formControl.currentValue;
    }
    if (verifyChange("invalid")) {
      this.invalid = Boolean(changes.invalid.currentValue);
      this.setError();
    }
    if (verifyChange("isShowLabelOutside")) {
      this.isShowLabelOutside = Boolean(changes.isShowLabelOutside.currentValue);
    }
    if (verifyChange("focus")) {
      this.focus = Boolean(changes.focus.currentValue);
    }
    if (verifyChange("disabled")) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.setDisabled();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // ensure when component is destroyed the subscription is also and not left open.
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-input-phone",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setError() {
    if (
      this.formGroup.controls[this.formControlName] !== undefined &&
      this.formGroup.controls[this.formControlName].invalid
    ) {
      this.invalid = true;
      this.focus = true;
    } else {
      this.invalid = false;
      this.focus = false;
    }
  }

  /**
   * @description Handles firing of 2 events on (keyup) event
   *
   * @param value
   */
  handleInputValueChange(value: any) {
    if (value === this.formControl.value) {
      this.emitEvent(this.getKeyValue, {
        key: this.formControlName,
        value
      });
    }
  }

  handleFocusedChange(e: any) {
    if (!this.invalid) {
      this.focus = e;
    }
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
